import { openDB } from 'idb';
import { ensureFreshToken } from '../api/tokenRefresh';

const DB_NAME = 'bfs_offline_vault';
const DB_VERSION = 1;
const STORE_NAME = 'pending_tickets';

// Serialize processQueue: online/interval/manual triggers can all fire it, and two overlapping
// runs would each getAll() the same pending set and re-POST every item (the delete only happens
// after success) — double-submitting. A module-level flag makes concurrent calls no-ops.
let isProcessing = false;

// Client-generated idempotency key. crypto.randomUUID exists in secure contexts (the app is HTTPS);
// the fallback keeps offline entry working anywhere.
const genKey = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Initialize the IndexedDB database
export const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    },
  });
};

/**
 * Queue a ticket payload in IndexedDB when offline.
 *
 * idempotencyKey MUST be the same key the caller used on the original (online) request, so that a
 * create whose response was lost — leaving a committed ticket the client never saw — is
 * de-duplicated server-side on replay instead of double-billing. For creates without a caller key
 * we generate one so at least repeated replays of THIS queued item dedup against each other.
 */
export const queueTicket = async (url, method, payload, idempotencyKey = null) => {
  try {
    const db = await initDB();
    await db.add(STORE_NAME, {
      url,
      method,
      payload,
      idempotencyKey: idempotencyKey || (method === 'POST' ? genKey() : null),
      timestamp: Date.now()
    });
    console.log('[OfflineSync] Ticket queued successfully in local vault.');
  } catch (error) {
    console.error('[OfflineSync] Failed to queue ticket:', error);
  }
};

/**
 * Get all queued tickets.
 */
export const getQueuedTickets = async () => {
  try {
    const db = await initDB();
    return await db.getAll(STORE_NAME);
  } catch (error) {
    console.error('[OfflineSync] Failed to get queued tickets:', error);
    return [];
  }
};

/**
 * Process the queue and send all pending tickets to the server.
 */
export const processQueue = async () => {
  if (!navigator.onLine) return;
  if (isProcessing) return;   // guard against overlapping runs double-submitting (see note above)
  isProcessing = true;

  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const pending = await store.getAll();

    if (pending.length === 0) return;

    console.log(`[OfflineSync] Processing ${pending.length} queued tickets...`);

    let syncCount = 0;
    // Attach the current access token so replayed writes aren't rejected 401 by the API.
    const token = await ensureFreshToken();
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    for (const item of pending) {
      try {
        const res = await fetch(item.url, {
          method: item.method,
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
            // Same key as the original attempt -> the server returns the already-created ticket
            // instead of inserting a duplicate. This is the double-billing guard (N01).
            ...(item.idempotencyKey ? { 'Idempotency-Key': item.idempotencyKey } : {})
          },
          body: JSON.stringify(item.payload)
        });

        if (res.ok) {
          // Only delete if successful
          const delTx = db.transaction(STORE_NAME, 'readwrite');
          await delTx.objectStore(STORE_NAME).delete(item.id);
          await delTx.done;
          syncCount++;
        } else {
          console.error(`[OfflineSync] Server rejected sync for item ${item.id}`, await res.text());
          // If 401 unauthorized, we should keep it and let the user log in
        }
      } catch (err) {
        console.error(`[OfflineSync] Network error during sync for item ${item.id}:`, err);
        // Still offline or CORS error, abort loop
        break;
      }
    }

    if (syncCount > 0) {
      console.log(`[OfflineSync] Successfully synced ${syncCount} tickets.`);
      // Dispatch a custom event so the UI knows it synced
      window.dispatchEvent(new CustomEvent('bfs-sync-complete', { detail: { count: syncCount } }));
    }
  } finally {
    isProcessing = false;
  }
};
